import { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  TrendUp, Truck, UsersThree, Warning, CurrencyDollar, Handshake, Sparkle, Broom, ChatCircleDots, CheckCircle,
} from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import TopPicksCard from "../components/TopPicksCard";
import BrokerCoachingCard from "../components/BrokerCoachingCard";
import BracketDriftCard from "../components/BracketDriftCard";
import CalibrationStreakWidget from "../components/CalibrationStreakWidget";
import BrokerOnboardingCard from "../components/BrokerOnboardingCard";
import DigestPreviewCard from "../components/DigestPreviewCard";
import ReadyToInvoiceCard from "../components/ReadyToInvoiceCard";
import DriverThanksLeaderboard from "../components/DriverThanksLeaderboard";
import FridaySaveWidget from "../components/FridaySaveWidget";
import OnboardingChecklist from "./OnboardingChecklist";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    const { data } = await client.get("/dashboard/stats");
    setStats(data);
  }, []);

  const loadFeedback = useCallback(async () => {
    try {
      const { data } = await client.get("/feedback/my-stats");
      setFeedbackStats(data);
    } catch {
      // silently skip — the widget just won't render
      setFeedbackStats(null);
    }
  }, []);

  useEffect(() => { load(); loadFeedback(); }, [load, loadFeedback]);

  const seedDemo = async () => {
    setSeeding(true);
    try {
      const { data } = await client.post("/demo/seed");
      toast.success(`Loaded sample data: ${data.counts.loads} loads, ${data.counts.drivers} drivers, ${data.counts.invoices} invoices`);
      await load();
    } catch { toast.error("Could not load sample data"); }
    finally { setSeeding(false); }
  };

  const clearDemo = async () => {
    if (!window.confirm("Remove all sample demo data? Your own records stay.")) return;
    setSeeding(true);
    try {
      const { data } = await client.post("/demo/clear");
      toast.success(`Removed ${data.removed} sample records`);
      await load();
    } catch { toast.error("Could not clear demo data"); }
    finally { setSeeding(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="dashboard-page">
      <PageHeader
        overline="MISSION CONTROL"
        title={`Good day, ${user?.full_name?.split(" ")[0] || "Operator"}.`}
        subtitle="Real-time overview of your fleet operations."
        action={
          stats && stats.total_loads === 0 ? (
            <button
              data-testid="dashboard-seed-btn"
              onClick={seedDemo}
              disabled={seeding}
              className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Sparkle size={14} weight="fill" /> {seeding ? "Loading..." : "Load sample data"}
            </button>
          ) : (
            <button
              data-testid="dashboard-clear-demo-btn"
              onClick={clearDemo}
              disabled={seeding}
              className="rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 border border-white/10 hover:border-white/25 text-neutral-400 hover:text-white transition-colors disabled:opacity-60"
              title="Remove sample demo data"
            >
              <Broom size={14} /> Clear demo data
            </button>
          )
        }
      />

      <OnboardingChecklist onDismiss={() => load()} />

      {/* Friday-Save Widget — auto-hides on non-Fridays / after 5pm / when clean */}
      <div className="mt-6">
        <FridaySaveWidget />
      </div>

      {/* Ready-to-Invoice — surfaces loads with fresh POD but no invoice yet */}
      <div className="mt-6">
        <ReadyToInvoiceCard />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 mt-10 card-surface overflow-hidden">
        <StatCell icon={<Truck size={20} />} label="ACTIVE LOADS"
          value={stats?.active_loads ?? "—"} sub={`of ${stats?.total_loads ?? 0} total`} testid="stat-active-loads" />
        <StatCell icon={<UsersThree size={20} />} label="DRIVERS AVAILABLE"
          value={stats?.available_drivers ?? "—"} sub={`of ${stats?.total_drivers ?? 0} on roster`} testid="stat-drivers" />
        <StatCell icon={<CurrencyDollar size={20} />} label="REVENUE PAID"
          value={stats ? `$${Number(stats.revenue_paid).toLocaleString()}` : "—"}
          sub={stats ? `$${Number(stats.outstanding).toLocaleString()} outstanding` : ""}
          testid="stat-revenue" accent />
        <StatCell icon={<Warning size={20} />} label="MAINTENANCE ALERTS"
          value={stats?.overdue_maintenance ?? "—"} sub="overdue items" testid="stat-maintenance" />
      </div>

      {/* Calibration Streak Widget — reinforces the AI Negotiation coaching loop.
          Auto-hides on fresh accounts with no closed bids yet. */}
      <div className="mt-4">
        <CalibrationStreakWidget />
      </div>

      {/* Broker onboarding nudge — surfaces top unverified customer name. */}
      <div className="mt-4">
        <BrokerOnboardingCard />
      </div>

      {/* Monday coaching digest preview — auto-hides for zero-activity fleets. */}
      <div className="mt-4">
        <DigestPreviewCard />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 card-surface p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline">RECENT LOADS</div>
              <h3 className="font-display text-xl font-semibold mt-1">Freight in motion</h3>
            </div>
            <TrendUp size={20} className="text-[#FF5A1F]" />
          </div>

          {stats?.recent_loads?.length ? (
            <div className="space-y-2">
              {stats.recent_loads.map((l) => (
                <div key={l.id} className="flex items-center justify-between p-3 rounded-md bg-[#0F0F0F] border border-white/5 hover-lift">
                  <div>
                    <div className="font-mono text-xs text-neutral-500">#{l.load_number}</div>
                    <div className="text-sm font-medium mt-0.5">
                      {l.origin} <span className="text-neutral-600">→</span> {l.destination}
                    </div>
                  </div>
                  <div className="text-right">
                    <StatusPill status={l.status} />
                    <div className="text-xs text-neutral-500 mt-1">{l.customer}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyDashboard onSeed={seedDemo} seeding={seeding} />
          )}
        </div>

        <div className="card-surface p-6">
          <div className="overline">PIPELINE</div>
          <h3 className="font-display text-xl font-semibold mt-1 mb-6">Growth signals</h3>

          <MiniStat icon={<Handshake size={16} />} label="New leads" value={stats?.new_leads ?? 0} />
          <MiniStat icon={<CurrencyDollar size={16} />} label="Outstanding invoices"
            value={stats ? `$${Number(stats.outstanding).toLocaleString()}` : "—"} />
          <MiniStat icon={<Warning size={16} />} label="Overdue maintenance" value={stats?.overdue_maintenance ?? 0} />

          <div className="mt-6 p-4 rounded-md border border-[#007AFF]/25 bg-[#007AFF]/5">
            <div className="overline text-[#60a5fa] mb-1">AI TIP</div>
            <p className="text-sm text-neutral-300 leading-relaxed">
              Use the AI Copilot to draft a bulk customer update whenever a lane experiences delay.
            </p>
          </div>
        </div>
      </div>

      {/* Top-Picks widget: appears only when the user has ≥1 saved-lane search.
          Fetches best STRONG/GOOD matches across all saved lanes so pilots see
          highest-value loads the moment they open the app. */}
      <div className="mt-6">
        <TopPicksCard />
      </div>

      {/* Broker Coaching Card — surfaces the AI Negotiator's coaching insights
          (same math as the Monday digest) whenever a broker's recent asks sit
          outside the learned bracket. Auto-hides on well-calibrated pilots. */}
      <div className="mt-6">
        <BrokerCoachingCard />
      </div>

      {/* Bracket Drift Alerts — pulses when a broker's floor/ceiling has
          shifted materially (>10%) in the last 30 days. Auto-hides when no
          drift is detected. */}
      <div className="mt-6">
        <BracketDriftCard />
      </div>

      {/* Driver Thanks Leaderboard — ranks drivers by fastest POD lag */}
      <div className="mt-6">
        <DriverThanksLeaderboard />
      </div>

      {/* Your Feedback panel — appears only after the user has submitted at least
          one piece of feedback, so it doesn't clutter first-run dashboards. */}
      {feedbackStats && feedbackStats.total > 0 && (
        <YourFeedbackCard stats={feedbackStats} />
      )}

    </div>
  );
}

function StatCell({ icon, label, value, sub, testid, accent }) {
  return (
    <div className="p-6 border-r border-b border-white/10 last:border-r-0" data-testid={testid}>
      <div className="flex items-center gap-2 text-neutral-500">{icon}<span className="overline">{label}</span></div>
      <div className={`font-display text-4xl font-semibold mt-4 tracking-tight ${accent ? "text-[#FF5A1F]" : ""}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
    </div>
  );
}

// Your Feedback stat card — appears only after user has submitted feedback.
// Closes the loop between "I sent feedback into the void" and "I see it being read + shipped".
function YourFeedbackCard({ stats }) {
  const shipped = stats.shipped || 0;
  const reviewed = stats.reviewed || 0;
  const total = stats.total || 0;
  const inFlight = total - shipped - (stats.wontfix || 0);
  return (
    <div
      className="mt-8 card-surface p-6 sm:p-8 relative overflow-hidden"
      data-testid="your-feedback-card"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-[#FF5A1F]/10 border border-[#FF5A1F]/25 flex items-center justify-center shrink-0">
          <ChatCircleDots size={22} weight="fill" className="text-[#FF5A1F]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[#FF9764] mb-1">YOUR FEEDBACK</div>
          <h3 className="font-display text-xl font-semibold mb-4">
            {shipped > 0
              ? `${shipped} of your ${total === 1 ? "idea" : "ideas"} shipped`
              : `${total} sent · we read every one`}
          </h3>
          <div className="grid grid-cols-3 gap-6 sm:gap-10">
            <FBStat label="Sent" value={total} tone="neutral" testid="your-fb-sent" />
            <FBStat label="Under review" value={inFlight} tone="sky" testid="your-fb-inflight" />
            <FBStat label="Shipped" value={shipped} tone="orange" testid="your-fb-shipped" hot={shipped > 0} />
          </div>
        </div>
      </div>
      {shipped > 0 && (
        <div className="mt-5 pt-5 border-t border-white/5 flex items-center gap-2 text-sm text-neutral-400">
          <CheckCircle size={14} weight="fill" className="text-emerald-400" />
          Check your email — we sent a note when each one shipped.
        </div>
      )}
    </div>
  );
}

function FBStat({ label, value, tone, testid, hot }) {
  const accents = {
    neutral: "text-white",
    sky: "text-sky-300",
    orange: "text-[#FF9764]",
  };
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5">{label}</div>
      <div className={`font-mono text-3xl sm:text-4xl font-semibold leading-none ${accents[tone] || accents.neutral}`}>
        {value}
        {hot && (
          <span className="text-[10px] font-semibold text-[#FF9764] bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 rounded-full px-1.5 py-0.5 ml-2 align-middle">
            🎉
          </span>
        )}
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-b-0">
      <div className="flex items-center gap-2 text-neutral-400 text-sm">{icon}{label}</div>
      <div className="font-mono text-sm font-semibold">{value}</div>
    </div>
  );
}

function EmptyDashboard({ onSeed, seeding }) {
  return (
    <div className="py-14 text-center">
      <div className="w-16 h-16 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center mx-auto mb-5">
        <Truck size={30} weight="regular" className="text-neutral-500" />
      </div>
      <div className="overline mb-2">NO LOADS YET</div>
      <h4 className="font-display text-2xl font-semibold">Your fleet is quiet</h4>
      <p className="text-neutral-400 mt-3 text-sm max-w-md mx-auto leading-relaxed">
        Load a fully-populated sample dataset to see how FreightPilot AI looks in action, or head straight to Dispatch to add your first real load.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <button
          data-testid="empty-seed-btn"
          onClick={onSeed}
          disabled={seeding}
          className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Sparkle size={14} weight="fill" /> {seeding ? "Loading..." : "Load sample data"}
        </button>
        <a href="/app/dispatch" className="rounded-full px-5 py-2.5 text-sm border border-white/15 hover:border-white/30 text-neutral-200 hover:text-white transition-colors">
          Add my first load
        </a>
      </div>
    </div>
  );
}
